/**
 * CategoryNavigationPropertiesEditor Component
 * 
 * Properties editor for the Category Navigation block.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryNavigationBlockProps } from '@/types/menuBuilder';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

interface CategoryNavigationPropertiesEditorProps {
  props: CategoryNavigationBlockProps;
  onUpdate: (props: Partial<CategoryNavigationBlockProps>) => void;
}

export const CategoryNavigationPropertiesEditor = memo(
  function CategoryNavigationPropertiesEditor({
    props,
    onUpdate,
  }: CategoryNavigationPropertiesEditorProps) {
    const { t } = useTranslation();

    return (
      <div className="space-y-6">
        {/* Sticky */}
        <div className="flex items-center justify-between">
          <Label htmlFor="sticky">
            {t('menuBuilder.categoryNav.sticky', 'Sticky Position')}
          </Label>
          <Switch
            id="sticky"
            checked={props.sticky}
            onCheckedChange={(checked) => onUpdate({ sticky: checked })}
          />
        </div>

        {/* Style */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.categoryNav.style', 'Style')}</Label>
          <Select
            value={props.style}
            onValueChange={(value: 'pills' | 'chips' | 'buttons') =>
              onUpdate({ style: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pills">
                {t('menuBuilder.categoryNav.pills', 'Pills (Rounded)')}
              </SelectItem>
              <SelectItem value="chips">
                {t('menuBuilder.categoryNav.chips', 'Chips (Square)')}
              </SelectItem>
              <SelectItem value="buttons">
                {t('menuBuilder.categoryNav.buttons', 'Buttons (Bordered)')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Size */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.categoryNav.size', 'Size')}</Label>
          <Select
            value={props.size}
            onValueChange={(value: 'sm' | 'md' | 'lg') =>
              onUpdate({ size: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sm">
                {t('menuBuilder.categoryNav.small', 'Small')}
              </SelectItem>
              <SelectItem value="md">
                {t('menuBuilder.categoryNav.medium', 'Medium')}
              </SelectItem>
              <SelectItem value="lg">
                {t('menuBuilder.categoryNav.large', 'Large')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Icon Size */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.categoryNav.iconSize', 'Icon Size')}</Label>
          <Select
            value={props.iconSize}
            onValueChange={(value: 'sm' | 'md' | 'lg') =>
              onUpdate({ iconSize: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sm">
                {t('menuBuilder.categoryNav.small', 'Small')}
              </SelectItem>
              <SelectItem value="md">
                {t('menuBuilder.categoryNav.medium', 'Medium')}
              </SelectItem>
              <SelectItem value="lg">
                {t('menuBuilder.categoryNav.large', 'Large')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Display Options */}
        <div className="space-y-4">
          <Label>{t('menuBuilder.displayOptions', 'Display Options')}</Label>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="showIcons" className="font-normal">
              {t('menuBuilder.categoryNav.showIcons', 'Show Icons')}
            </Label>
            <Switch
              id="showIcons"
              checked={props.showIcons}
              onCheckedChange={(checked) => onUpdate({ showIcons: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showNames" className="font-normal">
              {t('menuBuilder.categoryNav.showNames', 'Show Names')}
            </Label>
            <Switch
              id="showNames"
              checked={props.showNames}
              onCheckedChange={(checked) => onUpdate({ showNames: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="scrollToCategory" className="font-normal">
              {t('menuBuilder.categoryNav.scrollToCategory', 'Scroll to Category')}
            </Label>
            <Switch
              id="scrollToCategory"
              checked={props.scrollToCategory}
              onCheckedChange={(checked) =>
                onUpdate({ scrollToCategory: checked })
              }
            />
          </div>
        </div>

        {/* Background Color */}
        <div className="space-y-2">
          <Label htmlFor="backgroundColor">
            {t('menuBuilder.categoryNav.backgroundColor', 'Background Color')}
          </Label>
          <Input
            id="backgroundColor"
            type="text"
            value={props.backgroundColor || ''}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            placeholder="rgba(255, 255, 255, 0.95)"
          />
        </div>

        {/* Active Color */}
        <div className="space-y-2">
          <Label htmlFor="activeColor">
            {t('menuBuilder.categoryNav.activeColor', 'Active Color')}
          </Label>
          <Input
            id="activeColor"
            type="text"
            value={props.activeColor || ''}
            onChange={(e) => onUpdate({ activeColor: e.target.value })}
            placeholder="#000000"
          />
        </div>
      </div>
    );
  }
);
